/**
 * PipMascot — inline SVG mascot component for apps/web.
 *
 * Spec: .plan/features/2026-06-10-pip-mascot-placement.md §2
 * Asset source: tasks/mascot-options/pip.html (geometry in pipPaths.ts)
 * Token map (plan rev 3.1): var(--ink) → var(--text); --text is the canonical
 *   primary-ink token in packages/ui (--ink does NOT exist in production).
 *
 * Design rules (design.md §2 + §4.5 Pip amendment via T-P0):
 *   - CSS transform + opacity only. No d:path(), no SMIL, no JS animation lib.
 *   - prefers-reduced-motion → static resting pose (CSS media query; no JS).
 *   - Action fires via data-pip-action attribute on the SVG wrapper; CSS does
 *     the rest. Re-trigger: remove → reflow → re-add the wrapper class.
 *   - size < 32px → F6 Glyph cut (chunky stroke, dot eyes, poke only).
 *   - aria-hidden when decorative (beside a toast that already announces).
 *   - role="img" + <title>Pip</title> when standalone (empty state, 404).
 *   - No pure #ffffff or #000000 (warm neutrals only via design tokens).
 *   - No animation on demo/idle keyframes — those are doc-only per plan §1.
 *
 * Motion envelope (plan §0 / T-P0):
 *   Quiet actions ≤3.4 s per cycle, iteration cap 3.
 *   Peak actions ≤3 s, once-ever per moment key.
 *   Body deformation ≤22 %; positional overshoot ≤5 %.
 */

import { useEffect, useRef, type CSSProperties } from "react";
import "./pip.css";
import {
  VIEW_BOX,
  BODY,
  NUB_LEFT,
  NUB_RIGHT,
  POKE,
  EYES_CLOSED,
  EYES_OPEN,
  EYES_SAD,
  SMILE,
  BLUSH,
  SPECKS,
  GLYPH_BODY,
  GLYPH_POKE,
  GLYPH_EYES_OPEN,
} from "./pipPaths";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PipAction =
  "greet" | "congratulate" | "nod" | "knead" | "settle" | "promote" | "tumble" | "bounce";

export interface PipMascotProps {
  /** px height. Values < 32 render the F6 Glyph cut. */
  size: number;
  /** null = idle (proof-rise + blink at rest). */
  action?: PipAction | null;
  /** knead only: loop while true instead of 3 iterations. */
  loopWhile?: boolean;
  /** Fired when the action animation ends. */
  onActionEnd?: () => void;
  /**
   * a11y label for the SVG <title>.
   * Defaults to "Pip". Set to empty string if aria-hidden should be the only
   * accessibility treatment (e.g. beside a toast that already announces).
   */
  title?: string;
  /**
   * When true, aria-hidden="true" is added and the title is suppressed.
   * Use when Pip is purely decorative beside text that already describes
   * the surface (plan §2 a11y note).
   */
  ariaHidden?: boolean;
  /** Additional CSS class on the .proof-pip-mascot wrapper. */
  className?: string;
  style?: CSSProperties;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * PipMascot — inline SVG at the specified size.
 * Mount size controls which geometry cut renders:
 *   ≥ 32 px → F1 Classic + Nubs (hero cut)
 *   < 32 px → F6 Glyph (chunky stroke, dot eyes, poke only)
 */
export function PipMascot({
  size,
  action = null,
  loopWhile = false,
  onActionEnd,
  title = "Pip",
  ariaHidden = false,
  className,
  style,
}: PipMascotProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const isGlyph = size < 32;

  // ── Action trigger ─────────────────────────────────────────────────────────
  //
  // When action changes, remove the wrapper class, force a reflow, then
  // re-add. This restarts the CSS animations on the data-pip-action attribute
  // without needing JS animation APIs (plan §1 rule 1: no d:path(), plan §2:
  // CSS does the rest via data attribute).

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    if (!action) {
      el.removeAttribute("data-pip-action");
      el.removeAttribute("data-pip-loop");
      return;
    }

    // Remove → reflow → re-add to restart animations.
    el.removeAttribute("data-pip-action");
    el.removeAttribute("data-pip-loop");
    // Force reflow so the attribute removal takes effect before re-adding.
    void el.offsetWidth;
    el.setAttribute("data-pip-action", action);
    if (action === "knead" && loopWhile) {
      el.setAttribute("data-pip-loop", "");
    }
  }, [action, loopWhile]);

  // ── animationend → onActionEnd ─────────────────────────────────────────────

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !onActionEnd || !action) return;

    // For tumble/bounce the outermost animated group is .jump; for all others
    // it is .pip. Fire onActionEnd when the primary group's animation ends.
    const targetClass = action === "tumble" || action === "bounce" ? "jump" : "pip";

    function handleEnd(e: AnimationEvent) {
      const target = e.target as Element;
      if (target.classList.contains(targetClass)) {
        onActionEnd?.();
      }
    }

    el.addEventListener("animationend", handleEnd);
    return () => el.removeEventListener("animationend", handleEnd);
  }, [action, onActionEnd]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const svgSize = size;
  const a11yProps = ariaHidden
    ? { "aria-hidden": true as const, focusable: "false" as const }
    : { role: "img" as const };

  return (
    <div
      ref={wrapRef}
      className={`proof-pip-mascot${className ? ` ${className}` : ""}`}
      style={style}
      data-testid="pip-mascot"
    >
      <svg
        viewBox={VIEW_BOX}
        width={svgSize}
        height={svgSize}
        style={{ display: "block", overflow: "visible" }}
        {...a11yProps}
      >
        {!ariaHidden && title && <title>{title}</title>}
        {isGlyph ? (
          /* F6 Glyph cut — chunky stroke, dot eyes */
          <g className="pip">
            <path
              className="body"
              d={GLYPH_BODY}
              fill="var(--surface)"
              stroke="var(--text)"
              strokeWidth={3.5}
              strokeLinejoin="round"
            />
            <circle
              className="poke"
              cx={GLYPH_POKE.cx}
              cy={GLYPH_POKE.cy}
              r={GLYPH_POKE.r}
              fill="var(--accent)"
            />
            {GLYPH_EYES_OPEN.map((eye, i) => (
              <circle key={i} cx={eye.cx} cy={eye.cy} r={eye.r} fill="var(--text)" />
            ))}
          </g>
        ) : (
          /* F1 + Nubs hero cut — full detail */
          <g className="jump">
            <g className="spin">
              <g className="flex">
                <g className="pip">
                  <g className="specks">
                    {SPECKS.map((speck, i) => (
                      <circle
                        key={i}
                        className="speck"
                        cx={speck.cx}
                        cy={speck.cy}
                        r={speck.r}
                        fill={i === 1 ? "var(--accent)" : "var(--muted, #71685b)"}
                        opacity={0}
                      />
                    ))}
                  </g>
                  <path
                    className="body"
                    d={BODY}
                    fill="var(--surface)"
                    stroke="var(--text)"
                    strokeWidth={2.5}
                    strokeLinejoin="round"
                  />
                  <path
                    className="body"
                    d={NUB_LEFT}
                    fill="var(--surface)"
                    stroke="var(--text)"
                    strokeWidth={2.5}
                    strokeLinejoin="round"
                  />
                  <path
                    className="body"
                    d={NUB_RIGHT}
                    fill="var(--surface)"
                    stroke="var(--text)"
                    strokeWidth={2.5}
                    strokeLinejoin="round"
                  />
                  <circle
                    className="poke"
                    cx={POKE.cx}
                    cy={POKE.cy}
                    r={POKE.r}
                    fill="var(--accent)"
                  />
                  <g className="eyes-closed">
                    {EYES_CLOSED.map((d, i) => (
                      <path
                        key={i}
                        d={d}
                        fill="none"
                        stroke="var(--text)"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                      />
                    ))}
                  </g>
                  <g className="eyes-open">
                    {EYES_OPEN.map((eye, i) => (
                      <circle
                        key={i}
                        className="eye-dot"
                        cx={eye.cx}
                        cy={eye.cy}
                        r={eye.r}
                        fill="var(--text)"
                      />
                    ))}
                  </g>
                  <g className="eyes-sad">
                    {EYES_SAD.map((d, i) => (
                      <path
                        key={i}
                        d={d}
                        fill="none"
                        stroke="var(--text)"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                      />
                    ))}
                  </g>
                  <path
                    className="smile"
                    d={SMILE}
                    fill="none"
                    stroke="var(--text)"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                  />
                  {BLUSH.map((b, i) => (
                    <ellipse
                      key={i}
                      cx={b.cx}
                      cy={b.cy}
                      rx={b.rx}
                      ry={b.ry}
                      fill="color-mix(in srgb, var(--accent) 16%, var(--surface))"
                    />
                  ))}
                </g>
              </g>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
